// What a property management or inventory screen says when the module is
// locked (N3, PRD 42 Rule 4), drawn by the shell above the page.
//
// CRM's pattern, kept exactly (CrmModuleBanner): the api gates WRITES only, so
// the screen stays up, records stay readable, and this sits above them saying
// what stopped and the one button that fixes it. No route guard and no hidden
// nav row: a lease you cannot see is worse than one you cannot edit. While the
// subscription is paused there is nothing to buy, and it says so instead.
//
// INERT UNTIL THE API ENFORCES THE MODULE. It draws only when useModuleLock is
// true, which needs the module in the payload's `enforced_modules`. Neither
// module is there today, so this renders nothing for anybody.
//
// Placed by ROUTE, in the shell, rather than page by page, so a test can name
// every route of both modules in one place (moduleForPath).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { XStack, YStack } from 'tamagui';
import { GBAlert, GBButton, GBCard, GBText } from '@greenbackapp/ui';
import { useBusinessOptional } from '../../../contexts/BusinessContext';
import { useModuleLock } from '../../../hooks/useModuleLock';
import { BUSINESS_BILLING_PATH, PausedModulesNote, usePausedModuleOffer } from './pausedModules';

// The shell's banner margin, inside the component so an unlocked page gets no
// empty gap above it.
const Wrap = styled.div`
  margin: 16px 24px 0;
`;

type LockableModule = 'property_management' | 'inventory';

export const PROPERTY_MANAGEMENT_NOT_HELD_MESSAGE =
  "Property management is not on this business's subscription. Units, leases, deposits and "
  + 'maintenance already here stay readable and exportable; adding and editing them need the module.';

export const INVENTORY_NOT_HELD_MESSAGE =
  "Inventory is not on this business's subscription. Items and their history stay readable and "
  + 'exportable; adding items and adjusting stock need the module.';

const COPY: Record<LockableModule, { notHeld: string; paused: string; cta: string }> = {
  property_management: {
    notHeld: PROPERTY_MANAGEMENT_NOT_HELD_MESSAGE,
    paused: 'Property management is not available right now. Anything already here stays readable.',
    cta: 'Add the property management module',
  },
  inventory: {
    notHeld: INVENTORY_NOT_HELD_MESSAGE,
    paused: 'Inventory is not available right now. Anything already here stays readable.',
    cta: 'Add the inventory module',
  },
};

// Every web route whose page writes to one of the two modules. NOT the
// properties list (a property is core's tagging dimension, and adding one
// keeps working) and not rent roll, unit cost allocation or inventory
// valuation (reads).
const PREFIXES: Array<[string, LockableModule]> = [
  ['/business/leases', 'property_management'],
  ['/business/maintenance', 'property_management'],
  ['/business/settings/late-fees', 'property_management'],
  ['/business/settings/accounting-basis', 'property_management'],
  // The one report that writes: it posts each tenant's true-up.
  ['/business/reports/recovery-reconciliation', 'property_management'],
  ['/business/inventory', 'inventory'],
];

const PROPERTY_DETAIL = /^\/business\/properties\/[^/]+/;

// Read-only pages under a prefix above: the renewal pipeline is a query over
// leases with nothing to save, so it keeps working and says nothing.
const READ_ONLY_PAGES = ['/business/leases/renewals'];

/** The module a route's page writes to, or null. */
export function moduleForPath(pathname: string): LockableModule | null {
  if (READ_ONLY_PAGES.includes(pathname)) return null;
  const hit = PREFIXES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  if (hit) return hit[1];
  // A property's own page carries its units and cost pools.
  if (PROPERTY_DETAIL.test(pathname)) return 'property_management';
  return null;
}

const Banner: React.FC<{ moduleKey: LockableModule }> = ({ moduleKey }) => {
  const navigate = useNavigate();
  const offer = usePausedModuleOffer();
  const locked = useModuleLock(moduleKey);
  // A business that is read-only as a whole already has the shell's banner,
  // and buying a module is not the fix for that. The api orders the two
  // refusals the same way (access mode before module).
  const readOnly = useBusinessOptional()?.readOnly === true;
  if (!locked || readOnly) return null;
  const copy = COPY[moduleKey];

  return (
    <Wrap>
      <GBCard padding={16}>
        <YStack gap={8}>
          <GBAlert variant="info" message={offer.paused ? copy.paused : copy.notHeld} />
          {offer.paused ? (
            <PausedModulesNote offer={offer} />
          ) : (
            <XStack gap={8} alignItems="center" flexWrap="wrap">
              <GBButton size="sm" variant="primary" onPress={() => navigate(BUSINESS_BILLING_PATH)}>
                {copy.cta}
              </GBButton>
              <GBText variant="bodySmall" color="secondary">
                Owners add modules from Subscription &amp; billing.
              </GBText>
            </XStack>
          )}
        </YStack>
      </GBCard>
    </Wrap>
  );
};

export const ModuleLockBanner: React.FC<{ pathname: string }> = ({ pathname }) => {
  const moduleKey = moduleForPath(pathname);
  if (!moduleKey) return null;
  return <Banner moduleKey={moduleKey} />;
};
